import React, {
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import {
  AD_LANDING_LIMITS,
  emptyAdLandingContent,
  parseAdLandingContent,
  productRichTextToNodes,
  validateAdLandingForPublish,
  type AdLandingAssetDto,
  type AdLandingContent,
  type AdLandingDetail,
  type AdLandingList,
  type AdLandingPublishTarget,
  type AdLandingSummary,
  type MiniappPageLinkResponse,
  type MiniappUrlLink,
  type ProductRichTextRenderNode,
} from '@travel/contracts';
import { ApiError, type AdminApiClient } from './api-client';
import type { AdLandingEditorAsset } from './ad-landing-editor';
import './ad-landing-pages.css';

const AdLandingEditor = lazy(() =>
  import('./ad-landing-editor').then((module) => ({ default: module.AdLandingEditor })),
);

export interface AdLandingPagesProps {
  api: AdminApiClient;
  appId: string;
  appName?: string;
  mode: 'list' | 'new' | 'detail';
  landingId?: string;
  canManage: boolean;
  canPublish: boolean;
  onNavigate?: (href: string, targetAppId?: string) => void;
}

function message(cause: unknown) {
  if (cause instanceof ApiError) {
    if (cause.code === 'AD_LANDING_PUBLICATION_TARGET_FIXED')
      return '这个页面首次发布后已固定投放小程序。后续更新请继续发布到原小程序，原推广地址会保持不变。';
    if (cause.code === 'AD_LANDING_RELOCATED')
      return '这个草稿已发布到所选小程序，请返回列表或重新打开本页，前往已发布页面继续维护。';
    if (cause.code === 'AD_LANDING_CUSTOMER_SERVICE_DISABLED')
      return '所选小程序的在线客服尚未开启，请先开启客服后再发布。';
    if (cause.code === 'AD_LANDING_TARGET_UNAVAILABLE')
      return '所选小程序已停用或不可用，请取消本次发布后重新选择；草稿内容已保留。';
    if (cause.code === 'AD_LANDING_ASSET_COPY_FAILED')
      return '部分图片或视频读取、复制失败，原草稿已保留。请先重试；仍失败时重新上传相关素材后再发布。';
    if (cause.status === 403)
      return '当前账号没有执行此操作的权限，请联系管理员确认小程序和广告页权限。';
  }
  if (cause instanceof ApiError && cause.status === 409)
    return '这份页面已在其他窗口更新，当前输入已保留。请先备份当前内容，再重新读取后核对，避免覆盖他人的修改。';
  return cause instanceof Error ? cause.message : '操作未完成，请稍后重试；填写内容已保留。';
}
function basePath(appId: string) {
  return `/api/admin/apps/${encodeURIComponent(appId)}/ad-landings`;
}
function pagePath(appId: string, id: string) {
  return `${basePath(appId)}/${encodeURIComponent(id)}`;
}
function stamp(value: string | null) {
  const date = value ? new Date(value) : null;
  return date && Number.isFinite(date.getTime())
    ? date.toLocaleString('zh-CN', {
        hour12: false,
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';
}
function Status({ page }: { page: AdLandingSummary }) {
  if (page.relocatedTo) return <span className="ad-status">已转移发布</span>;
  const published = page.status === 'PUBLISHED';
  return (
    <span className={`ad-status${published ? ' ad-status--published' : ''}`}>
      {{ DRAFT: '草稿', PUBLISHED: '已发布', OFFLINE: '已停用', ARCHIVED: '已归档' }[page.status]}
      {published && page.hasUnpublishedChanges ? ' · 有新修改' : ''}
    </span>
  );
}
function publicationName(page: AdLandingSummary, appName?: string) {
  return page.relocatedTo
    ? '已转移至其他小程序'
    : page.currentPublishedVersionNumber === null
      ? '尚未发布'
      : appName || '当前小程序';
}

function Dialog({
  title,
  children,
  onClose,
  wide = false,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
}) {
  const host = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const before = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const target = host.current;
    target
      ?.querySelector<HTMLElement>(
        'button:not(:disabled),input:not(:disabled),select:not(:disabled),[tabindex="0"]',
      )
      ?.focus();
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close.current();
      }
      if (event.key !== 'Tab' || !target) return;
      const controls = Array.from(
        target.querySelectorAll<HTMLElement>(
          'button:not(:disabled),input:not(:disabled),select:not(:disabled),[tabindex="0"]',
        ),
      );
      const first = controls[0],
        last = controls[controls.length - 1];
      if (!target.contains(document.activeElement)) {
        event.preventDefault();
        first?.focus();
        return;
      }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('keydown', key);
      before?.focus();
    };
  }, []);
  return (
    <div
      className="ad-modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={host}
        className={`ad-modal${wide ? ' ad-preview-modal' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        {!wide && <h2>{title}</h2>}
        {children}
      </div>
    </div>
  );
}

function AdLandingListPage(props: AdLandingPagesProps) {
  const [search, setSearch] = useState(''),
    [status, setStatus] = useState('');
  const [query, setQuery] = useState({ search: '', status: '', page: 1 });
  const [revision, setRevision] = useState(0);
  const [offlinePage, setOfflinePage] = useState<AdLandingSummary>();
  const [offlineBusy, setOfflineBusy] = useState(false);
  const [offlineError, setOfflineError] = useState('');
  const offlineAttempt = useRef<{ fingerprint: string; key: string }>();
  const offlinePending = useRef(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const [state, setState] = useState<{ loading: boolean; data?: AdLandingList; error?: string }>({
    loading: true,
  });
  useEffect(() => {
    const abort = new AbortController();
    setState({ loading: true });
    const params = new URLSearchParams({
      page: String(query.page),
      pageSize: '20',
      search: query.search,
      status: query.status,
    });
    void props.api
      .request<AdLandingList>(`${basePath(props.appId)}?${params}`, {
        signal: abort.signal,
        cache: 'no-store',
      })
      .then((data) => {
        if (!abort.signal.aborted) setState({ loading: false, data });
      })
      .catch((cause: unknown) => {
        if (!abort.signal.aborted) setState({ loading: false, error: message(cause) });
      });
    return () => abort.abort();
  }, [props.api, props.appId, query, revision]);
  const navigate =
    props.onNavigate ??
    ((href: string, targetAppId = props.appId) => {
      const target = new URL(href, location.origin);
      target.searchParams.set('appId', targetAppId);
      location.assign(`${target.pathname}${target.search}${target.hash}`);
    });
  async function offline() {
    if (!props.canPublish || !offlinePage || offlinePending.current) return;
    offlinePending.current = true;
    setOfflineBusy(true);
    setOfflineError('');
    try {
      const body = { expectedPublishedVersion: offlinePage.currentPublishedVersionNumber ?? 0 };
      const fingerprint = JSON.stringify({ appId: props.appId, pageId: offlinePage.id, body });
      if (offlineAttempt.current?.fingerprint !== fingerprint)
        offlineAttempt.current = { fingerprint, key: crypto.randomUUID() };
      const value = await props.api.request<AdLandingDetail>(
        `${pagePath(props.appId, offlinePage.id)}/offline`,
        {
          method: 'POST',
          headers: { 'Idempotency-Key': offlineAttempt.current.key },
          body: JSON.stringify(body),
        },
      );
      if (
        value.page.appId !== props.appId ||
        value.page.id !== offlinePage.id ||
        value.page.status !== 'OFFLINE'
      )
        throw new Error('停用结果异常，请刷新列表核对。');
      if (!alive.current) return;
      offlineAttempt.current = undefined;
      setOfflinePage(undefined);
      setRevision((value) => value + 1);
    } catch (cause) {
      if (alive.current) setOfflineError(message(cause));
    } finally {
      offlinePending.current = false;
      if (alive.current) setOfflineBusy(false);
    }
  }
  const actions = (row: AdLandingSummary) => (
    <div className="ad-row-actions">
      {row.relocatedTo ? (
        <button
          type="button"
          onClick={() =>
            navigate(
              `/admin/ad-landings/${encodeURIComponent(row.relocatedTo!.pageId)}`,
              row.relocatedTo!.appId,
            )
          }
        >
          前往已发布页面
        </button>
      ) : (
        <>
          <button
            type="button"
            onClick={() => navigate(`/admin/ad-landings/${encodeURIComponent(row.id)}`)}
          >
            {props.canManage ? '编辑' : '查看'}
          </button>
          <button
            type="button"
            onClick={() => navigate(`/admin/ad-landings/${encodeURIComponent(row.id)}#promotion`)}
          >
            推广地址
          </button>
          {props.canPublish && row.status === 'PUBLISHED' && (
            <button
              type="button"
              onClick={() => {
                setOfflinePage(row);
                setOfflineError('');
              }}
            >
              停用
            </button>
          )}
        </>
      )}
    </div>
  );
  const data = state.data;
  return (
    <>
      <header className="ad-heading">
        <div>
          <h1>落地页管理</h1>
        </div>
        {props.canManage && (
          <button
            type="button"
            className="ad-primary"
            onClick={() => navigate('/admin/ad-landings/new')}
          >
            ＋ 新建落地页
          </button>
        )}
      </header>
      <form
        className="ad-filterbar"
        onSubmit={(event) => {
          event.preventDefault();
          setQuery({ search: search.trim(), status, page: 1 });
        }}
      >
        <label>
          搜索页面
          <input
            aria-label="搜索页面"
            maxLength={100}
            placeholder="输入页面标题"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <label>
          状态
          <select
            aria-label="落地页状态"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="">全部状态</option>
            <option value="DRAFT">草稿</option>
            <option value="PUBLISHED">已发布</option>
            <option value="OFFLINE">已停用</option>
            <option value="ARCHIVED">已归档</option>
          </select>
        </label>
        <button type="submit">查询</button>
        {(query.search || query.status) && (
          <button
            type="button"
            onClick={() => {
              setSearch('');
              setStatus('');
              setQuery({ search: '', status: '', page: 1 });
            }}
          >
            清空筛选
          </button>
        )}
      </form>
      {state.loading ? (
        <div className="ad-empty" role="status">
          正在读取落地页…
        </div>
      ) : state.error ? (
        <div className="ad-alert ad-alert--error" role="alert">
          {state.error}{' '}
          <button type="button" onClick={() => setRevision((value) => value + 1)}>
            重试
          </button>
        </div>
      ) : !data?.items.length ? (
        <div className="ad-empty">
          <h2>{query.search || query.status ? '没有找到符合条件的落地页' : '还没有落地页'}</h2>
          <p>
            {query.search || query.status
              ? '换个关键词或清空筛选再看看。'
              : '写好介绍并发布，就能把客户带到专属页面。'}
          </p>
          {props.canManage && !query.search && !query.status && (
            <button
              type="button"
              className="ad-primary"
              onClick={() => navigate('/admin/ad-landings/new')}
            >
              新建落地页
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="ad-table-scroll">
            <table className="ad-table">
              <thead>
                <tr>
                  <th>页面标题</th>
                  <th>状态</th>
                  <th>投放小程序</th>
                  <th>最近修改</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <span className="ad-page-title">{row.title || '未命名草稿'}</span>
                      <small className="ad-muted">{row.summary || '暂无分享简介'}</small>
                    </td>
                    <td>
                      <Status page={row} />
                    </td>
                    <td>
                      <span className="ad-publication-name">
                        {publicationName(row, props.appName)}
                      </span>
                      <small className="ad-muted">
                        {row.relocatedTo
                          ? '请前往已发布页面'
                          : row.currentPublishedVersionNumber === null
                            ? '首次发布时选择'
                            : '已固定投放归属'}
                      </small>
                    </td>
                    <td>{stamp(row.updatedAt)}</td>
                    <td>{actions(row)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="ad-mobile-list">
            {data.items.map((row) => (
              <article className="ad-mobile-record" key={row.id}>
                <span className="ad-page-title">{row.title || '未命名草稿'}</span>
                <Status page={row} />
                <p>投放小程序：{publicationName(row, props.appName)}</p>
                <p>最近修改 {stamp(row.updatedAt)}</p>
                {actions(row)}
              </article>
            ))}
          </div>
          <footer className="ad-pager">
            <span>
              共 {data.total} 个页面 · 第 {data.page} 页
            </span>
            <div className="ad-inline-actions">
              <button
                disabled={query.page <= 1}
                onClick={() => setQuery((current) => ({ ...current, page: current.page - 1 }))}
              >
                上一页
              </button>
              <button
                disabled={data.page * data.pageSize >= data.total}
                onClick={() => setQuery((current) => ({ ...current, page: current.page + 1 }))}
              >
                下一页
              </button>
            </div>
          </footer>
        </>
      )}
      {offlinePage && (
        <Dialog
          title="停用这个页面？"
          onClose={() => {
            if (!offlinePending.current) setOfflinePage(undefined);
          }}
        >
          <p>停用后客户将无法打开此页，内容会保留。</p>
          {offlineError && (
            <p className="ad-alert ad-alert--error" role="alert">
              {offlineError}
            </p>
          )}
          <div className="ad-inline-actions">
            <button type="button" disabled={offlineBusy} onClick={() => setOfflinePage(undefined)}>
              取消
            </button>
            <button
              type="button"
              className="ad-danger"
              disabled={offlineBusy}
              onClick={() => void offline()}
            >
              {offlineBusy ? '正在停用…' : '确认停用'}
            </button>
          </div>
        </Dialog>
      )}
    </>
  );
}

function CopyRow({
  label,
  value,
  hint,
  children,
}: {
  label: string;
  value: string | null;
  hint?: string;
  children?: ReactNode;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState('');
  useEffect(() => setCopied(''), [value]);
  return (
    <div className="ad-link-row">
      <label>{label}</label>
      <div className="ad-copy-row">
        <input ref={input} aria-label={label} value={value ?? ''} readOnly placeholder="尚未生成" />
        {children}
        <button
          type="button"
          disabled={!value}
          onClick={() => {
            if (!value) return;
            if (navigator.clipboard?.writeText)
              void navigator.clipboard
                .writeText(value)
                .then(() => setCopied('已复制'))
                .catch(() => {
                  input.current?.focus();
                  input.current?.select();
                  setCopied('已选中文本，请按 Ctrl+C 复制');
                });
            if (!navigator.clipboard?.writeText) {
              input.current?.focus();
              input.current?.select();
              setCopied('已选中文本，请按 Ctrl+C 复制');
            }
          }}
        >
          复制
        </button>
      </div>
      {copied ? <small role="status">{copied}</small> : hint ? <small>{hint}</small> : null}
    </div>
  );
}

type Links = MiniappPageLinkResponse & { urlScheme?: MiniappUrlLink | null };
function AdLandingPromotion({
  api,
  appId,
  page,
}: {
  api: AdminApiClient;
  appId: string;
  page: AdLandingSummary;
}) {
  const [environment, setEnvironment] = useState<'release' | 'trial'>('release');
  const [entry, setEntry] = useState<Links>();
  const [error, setError] = useState(''),
    [loading, setLoading] = useState(false);
  const [clock, setClock] = useState(Date.now());
  const [generating, setGenerating] = useState('');
  const [refresh, setRefresh] = useState(0);
  const generation = useRef(0);
  const command = useRef<AbortController>();
  const base = `/api/admin/m6/apps/${encodeURIComponent(appId)}/page-links`;
  useEffect(() => {
    const id = ++generation.current,
      abort = new AbortController();
    command.current?.abort();
    setEntry(undefined);
    setError('');
    setLoading(true);
    setGenerating('');
    const timeout = setTimeout(() => {
      if (generation.current !== id) return;
      abort.abort();
      setLoading(false);
      setError('读取推广信息超时，请重新读取；已保存内容不受影响。');
    }, 12_000);
    const query = new URLSearchParams({
      targetType: 'AD_LANDING',
      targetId: page.id,
      environmentVersion: environment,
    });
    void Promise.all(
      ['URL_LINK', 'URL_SCHEME'].map((kind) =>
        api.request<Links>(`${base}?${query}&linkKind=${kind}`, {
          signal: abort.signal,
          cache: 'no-store',
        }),
      ),
    )
      .then(([link, scheme]) => {
        if (!abort.signal.aborted && generation.current === id && link)
          setEntry({ ...link, urlScheme: scheme?.urlScheme ?? null });
      })
      .catch((cause: unknown) => {
        if (!abort.signal.aborted && generation.current === id) setError(message(cause));
      })
      .finally(() => {
        clearTimeout(timeout);
        if (!abort.signal.aborted && generation.current === id) setLoading(false);
      });
    return () => {
      clearTimeout(timeout);
      generation.current++;
      abort.abort();
      command.current?.abort();
    };
  }, [api, base, page.id, page.status, page.currentPublishedVersionNumber, environment, refresh]);
  useEffect(() => {
    const expiries = [entry?.urlLink, entry?.urlScheme]
      .flatMap((link) => (link ? [new Date(link.expiresAt).getTime()] : []))
      .filter((time) => Number.isFinite(time) && time > Date.now());
    if (!expiries.length) return;
    const timer = setTimeout(
      () => setClock(Date.now()),
      Math.min(Math.min(...expiries) - Date.now() + 20, 2_147_483_647),
    );
    return () => clearTimeout(timer);
  }, [entry, clock]);
  async function generate(kind: 'URL_LINK' | 'URL_SCHEME') {
    if (generating || command.current || !entry?.available) return;
    const id = generation.current;
    const abort = new AbortController();
    command.current = abort;
    const timeout = setTimeout(() => abort.abort(), 20_000);
    setGenerating(kind);
    setError('');
    try {
      const value = await api.request<Links>(base, {
        method: 'POST',
        signal: abort.signal,
        body: JSON.stringify({
          targetType: 'AD_LANDING',
          targetId: page.id,
          environmentVersion: environment,
          linkKind: kind,
        }),
      });
      if (generation.current === id)
        setEntry((current) => ({
          ...current,
          ...value,
          urlLink: kind === 'URL_LINK' ? value.urlLink : (current?.urlLink ?? null),
          urlScheme:
            kind === 'URL_SCHEME' ? (value.urlScheme ?? null) : (current?.urlScheme ?? null),
        }));
    } catch (cause) {
      if (generation.current === id)
        setError(
          abort.signal.aborted ? '微信地址生成超时，可重试；已保存内容不受影响。' : message(cause),
        );
    } finally {
      clearTimeout(timeout);
      if (command.current === abort) command.current = undefined;
      if (generation.current === id) setGenerating('');
    }
  }
  const usable = (link: MiniappUrlLink | null | undefined) =>
    link &&
    link.environmentVersion === environment &&
    new Date(link.expiresAt).getTime() > Math.max(clock, Date.now()) &&
    entry?.available
      ? link
      : null;
  const urlLink = usable(entry?.urlLink),
    scheme = usable(entry?.urlScheme);
  const expiry = (value: MiniappUrlLink | null) =>
    value
      ? `有效至 ${new Date(value.expiresAt).toLocaleString('zh-CN', { hour12: false })}`
      : undefined;
  return (
    <section id="promotion" className="ad-card" aria-label="推广地址">
      <div className="ad-link-header">
        <div>
          <h2>推广地址</h2>
          <p className="ad-muted">三种方式都在所属小程序内打开此页，展示已发布内容。</p>
        </div>
        <label>
          打开版本
          <select
            aria-label="推广打开版本"
            value={environment}
            disabled={Boolean(generating)}
            onChange={(event) => setEnvironment(event.target.value as 'release' | 'trial')}
          >
            <option value="release">正式版（广告投放）</option>
            <option value="trial">体验版（测试）</option>
          </select>
        </label>
      </div>
      {loading && <p role="status">正在读取推广信息…</p>}
      {error && (
        <p role="alert" className="ad-alert ad-alert--error">
          {error}{' '}
          <button
            type="button"
            disabled={Boolean(generating)}
            onClick={() => setRefresh((value) => value + 1)}
          >
            重新读取
          </button>
        </p>
      )}
      {entry?.wechatAppId && <p className="ad-muted">小程序 AppID：{entry.wechatAppId}</p>}
      {!entry?.available && !loading && (
        <p className="ad-alert">{entry?.reason || '保存并发布页面后，即可获取正式推广地址。'}</p>
      )}
      <CopyRow
        label="小程序页面路径"
        value={entry?.available ? entry.pathWithQuery : null}
        hint="广告平台要求页面路径时，复制这一项。"
      />
      <CopyRow
        label="微信 URL Link"
        value={urlLink?.url ?? null}
        {...(expiry(urlLink) ? { hint: expiry(urlLink)! } : {})}
      >
        <button
          type="button"
          disabled={loading || Boolean(generating) || !entry?.available}
          onClick={() => void generate('URL_LINK')}
        >
          {generating === 'URL_LINK' ? '生成中…' : urlLink ? '更新' : '生成'}
        </button>
      </CopyRow>
      <CopyRow
        label="微信 URL Scheme"
        value={scheme?.url ?? null}
        {...(expiry(scheme) ? { hint: expiry(scheme)! } : {})}
      >
        <button
          type="button"
          disabled={loading || Boolean(generating) || !entry?.available}
          onClick={() => void generate('URL_SCHEME')}
        >
          {generating === 'URL_SCHEME' ? '生成中…' : scheme ? '更新' : '生成'}
        </button>
      </CopyRow>
      <p className="ad-muted">
        {environment === 'release'
          ? '正式投放前，请确认微信正式版已包含此页面。链接由微信生成，生成失败不影响已保存内容。'
          : '体验版只供已获授权的体验成员测试，不能当作正式投放地址。'}
      </p>
    </section>
  );
}

function textNodes(nodes: ProductRichTextRenderNode[]): ReactNode {
  return nodes.map((node, index) => {
    if (node.type === 'text') return node.text;
    const style = Object.fromEntries(
      (node.attrs.style ?? '').split(';').flatMap((part) => {
        const separator = part.indexOf(':');
        if (separator < 1) return [];
        const property = part
          .slice(0, separator)
          .trim()
          .replace(/-([a-z])/gu, (_, letter: string) => letter.toUpperCase());
        return [[property, part.slice(separator + 1).trim()]];
      }),
    ) as CSSProperties;
    return React.createElement(
      node.name,
      {
        key: index,
        style,
        ...(node.attrs.start ? { start: Number(node.attrs.start) } : {}),
        ...(node.attrs.type ? { type: node.attrs.type } : {}),
      },
      node.children.length ? textNodes(node.children) : undefined,
    );
  });
}
function Preview({
  content,
  assets,
  onClose,
  onPreviewAsset,
}: {
  content: AdLandingContent;
  assets: AdLandingEditorAsset[];
  onClose: () => void;
  onPreviewAsset: (id: string) => void;
}) {
  const lookup = new Map(assets.map((asset) => [asset.id, asset.url]));
  return (
    <Dialog title="当前草稿预览" wide onClose={onClose}>
      <header className="ad-preview-top">
        <div>
          <strong>当前草稿预览</strong>
          <p className="ad-muted">查看当前输入，尚未公开发布</p>
        </div>
        <button type="button" onClick={onClose}>
          关闭
        </button>
      </header>
      <div className="ad-preview-phone">
        <article className="ad-preview-content">
          <h1>{content.title || '页面标题'}</h1>
          {content.summary && <p>{content.summary}</p>}
          {content.blocks.map((block) =>
            block.type === 'richText' ? (
              <div key={block.id} className="ad-preview-text">
                {textNodes(productRichTextToNodes(block.document, () => null))}
              </div>
            ) : (
              <figure
                key={block.id}
                style={{
                  width: block.width,
                  marginLeft: block.align === 'left' ? 0 : 'auto',
                  marginRight: block.align === 'right' ? 0 : 'auto',
                }}
              >
                {lookup.get(block.assetId) ? (
                  block.type === 'image' ? (
                    <img src={lookup.get(block.assetId)} alt={block.alt || '页面图片'} />
                  ) : (
                    <video
                      src={lookup.get(block.assetId)}
                      poster={block.posterAssetId ? lookup.get(block.posterAssetId) : undefined}
                      controls
                      preload="metadata"
                      playsInline
                    />
                  )
                ) : (
                  <div className="ad-media-unavailable">
                    {block.type === 'video' ? (
                      <button type="button" onClick={() => onPreviewAsset(block.assetId)}>
                        ▶ 加载视频预览
                      </button>
                    ) : (
                      '图片正在读取…'
                    )}
                  </div>
                )}
                {block.type === 'image' && block.alt && <figcaption>{block.alt}</figcaption>}
              </figure>
            ),
          )}
        </article>
        <footer className="ad-preview-consult">
          <span>在线咨询</span>
          <small>微信内点击后直接进入本小程序的客服会话</small>
        </footer>
      </div>
    </Dialog>
  );
}

function AdLandingEditorPage(props: AdLandingPagesProps) {
  const [detail, setDetail] = useState<AdLandingDetail>();
  const detailRef = useRef<AdLandingDetail>();
  const [content, setContent] = useState<AdLandingContent>(() => emptyAdLandingContent());
  const contentRef = useRef(content);
  contentRef.current = content;
  const [saved, setSaved] = useState(() => JSON.stringify(emptyAdLandingContent()));
  const [loading, setLoading] = useState(props.mode === 'detail');
  const [loadError, setLoadError] = useState(''),
    [error, setError] = useState(''),
    [feedback, setFeedback] = useState('');
  const [validationError, setValidationError] = useState('');
  const [action, setAction] = useState(''),
    [mediaBusy, setMediaBusy] = useState(false);
  const [preview, setPreview] = useState(false);
  const [publishTargetId, setPublishTargetId] = useState('');
  const [targetTouched, setTargetTouched] = useState(false);
  const publishTargetInput = useRef<HTMLSelectElement>(null);
  const [publishTargets, setPublishTargets] = useState<{
    loading: boolean;
    items: AdLandingPublishTarget[];
    error?: string;
  }>({ loading: true, items: [] });
  const [targetRevision, setTargetRevision] = useState(0);
  const publishAttempt = useRef<{ fingerprint: string; key: string }>();
  const [leave, setLeave] = useState<{ commit: () => void }>();
  const [assetUrls, setAssetUrls] = useState<Record<string, string>>({});
  const [mediaError, setMediaError] = useState('');
  const urls = useRef(new Map<string, string>()),
    pendingAssets = useRef(new Set<string>());
  const alive = useRef(true),
    busyRef = useRef(false),
    mediaBusyRef = useRef(false),
    navigationBypass = useRef(false);
  const creation = useRef<Promise<AdLandingDetail>>();
  const [reload, setReload] = useState(0);
  const targetLocked = detail?.page.currentPublishedVersionNumber != null;
  const contentDirty = JSON.stringify(content) !== saved || Boolean(validationError);
  const contentDirtyRef = useRef(contentDirty);
  contentDirtyRef.current = contentDirty;
  const dirty = contentDirty || (!targetLocked && targetTouched && Boolean(publishTargetId));
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const route =
    props.onNavigate ??
    ((href: string, targetAppId = props.appId) => {
      const target = new URL(href, location.origin);
      target.searchParams.set('appId', targetAppId);
      location.assign(`${target.pathname}${target.search}${target.hash}`);
    });
  const blocked = Boolean(action) || mediaBusy;
  const applyDetail = (value: AdLandingDetail, replaceContent: boolean) => {
    if (
      value.page.appId !== props.appId ||
      (detailRef.current && detailRef.current.page.id !== value.page.id)
    )
      throw new Error('返回页面的归属不一致，当前输入已保留，请重新读取。');
    detailRef.current = value;
    setDetail(value);
    if (replaceContent) {
      contentRef.current = value.page.draftContent;
      setContent(value.page.draftContent);
      setSaved(JSON.stringify(value.page.draftContent));
      dirtyRef.current = false;
    }
  };
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      urls.current.forEach((url) => URL.revokeObjectURL(url));
      urls.current.clear();
    };
  }, []);
  useEffect(() => {
    if (props.mode !== 'detail' || !props.landingId) return;
    const abort = new AbortController();
    let redirecting = false;
    setLoading(true);
    setLoadError('');
    void props.api
      .request<AdLandingDetail>(pagePath(props.appId, props.landingId), {
        signal: abort.signal,
        cache: 'no-store',
      })
      .then((value) => {
        if (abort.signal.aborted) return;
        if (value.page.appId !== props.appId || value.page.id !== props.landingId)
          throw new Error('页面所属小程序不一致，请重新选择。');
        if (value.page.relocatedTo) {
          const target = value.page.relocatedTo;
          if (
            !target.pageId ||
            !target.appId ||
            (target.pageId === value.page.id && target.appId === value.page.appId)
          )
            throw new Error('已发布页面的跳转信息异常，请从落地页管理重新打开。');
          redirecting = true;
          commitNavigation(() =>
            route(`/admin/ad-landings/${encodeURIComponent(target.pageId)}`, target.appId),
          );
          return;
        }
        applyDetail(value, true);
      })
      .catch((cause: unknown) => {
        if (!abort.signal.aborted) setLoadError(message(cause));
      })
      .finally(() => {
        if (!abort.signal.aborted && !redirecting) setLoading(false);
      });
    return () => abort.abort();
  }, [props.api, props.appId, props.mode, props.landingId, reload]);
  useEffect(() => {
    if (loading || targetLocked) return;
    if ((!props.canManage && !props.canPublish) || detail?.page.status === 'ARCHIVED') {
      setPublishTargets({ loading: false, items: [] });
      return;
    }
    const abort = new AbortController();
    setPublishTargets({ loading: true, items: [] });
    const timeout = setTimeout(() => {
      abort.abort();
      setPublishTargets({ loading: false, items: [], error: '读取可发布小程序超时，请重试。' });
    }, 12_000);
    void props.api
      .request<{ items: AdLandingPublishTarget[] }>('/api/admin/ad-landings/publish-targets', {
        signal: abort.signal,
        cache: 'no-store',
      })
      .then((value) => {
        if (abort.signal.aborted) return;
        if (
          !Array.isArray(value?.items) ||
          value.items.some(
            (target) =>
              !target ||
              typeof target.id !== 'string' ||
              !target.id ||
              typeof target.displayName !== 'string' ||
              !target.displayName,
          )
        )
          throw new Error('可发布小程序信息不完整，请重新读取。');
        setPublishTargets({ loading: false, items: value.items });
        setPublishTargetId((current) =>
          current
            ? value.items.some((target) => target.id === current)
              ? current
              : ''
            : value.items.length === 1
              ? value.items[0]!.id
              : '',
        );
      })
      .catch((cause: unknown) => {
        if (!abort.signal.aborted)
          setPublishTargets({ loading: false, items: [], error: message(cause) });
      })
      .finally(() => clearTimeout(timeout));
    return () => {
      clearTimeout(timeout);
      abort.abort();
    };
  }, [
    props.api,
    props.canManage,
    props.canPublish,
    targetRevision,
    targetLocked,
    loading,
    detail?.page.status,
  ]);
  useEffect(() => {
    if (!loading && detail && location.hash === '#promotion')
      document.getElementById('promotion')?.scrollIntoView?.({ block: 'start' });
  }, [loading, detail?.page.id]);
  async function loadAsset(asset: AdLandingAssetDto): Promise<string> {
    if (asset.appId !== props.appId || asset.pageId !== detailRef.current?.page.id)
      throw new Error('素材所属页面不一致，已停止读取');
    const cached = urls.current.get(asset.id);
    if (cached) return cached;
    const blob = await props.api.download(asset.adminContentUrl);
    if (!alive.current) throw new Error('页面已切换，请在当前页面重新操作');
    const url = URL.createObjectURL(blob);
    urls.current.set(asset.id, url);
    setAssetUrls((current) => ({ ...current, [asset.id]: url }));
    return url;
  }
  useEffect(() => {
    for (const asset of detail?.assets ?? []) {
      if (
        asset.state !== 'READY' ||
        asset.kind !== 'IMAGE' ||
        urls.current.has(asset.id) ||
        pendingAssets.current.has(asset.id)
      )
        continue;
      pendingAssets.current.add(asset.id);
      void loadAsset(asset)
        .catch((cause: unknown) => {
          if (alive.current) setMediaError(`${asset.originalName}：${message(cause)}`);
        })
        .finally(() => pendingAssets.current.delete(asset.id));
    }
  }, [detail?.assets]);
  const assets: AdLandingEditorAsset[] = (detail?.assets ?? [])
    .filter((asset) => asset.state === 'READY')
    .map((asset) => ({
      id: asset.id,
      kind: asset.kind,
      name: asset.originalName,
      ...(assetUrls[asset.id] ? { url: assetUrls[asset.id]! } : {}),
    }));
  function previewAsset(id: string) {
    const asset = detailRef.current?.assets.find(
      (value) => value.id === id && value.state === 'READY',
    );
    if (!asset || pendingAssets.current.has(id)) return;
    pendingAssets.current.add(id);
    setMediaError('');
    void loadAsset(asset)
      .catch((cause: unknown) => {
        if (alive.current) setMediaError(message(cause));
      })
      .finally(() => pendingAssets.current.delete(id));
  }
  useEffect(() => {
    const listener = (event: Event) => {
      if (navigationBypass.current) {
        navigationBypass.current = false;
        return;
      }
      if (!dirtyRef.current && !busyRef.current && !mediaBusyRef.current) return;
      const commit = (event as CustomEvent<{ commit?: () => void }>).detail?.commit;
      if (typeof commit === 'function') {
        event.preventDefault();
        setLeave({ commit });
      }
    };
    window.addEventListener('travel:admin-navigation', listener);
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (navigationBypass.current) return;
      if (!dirtyRef.current && !busyRef.current && !mediaBusyRef.current) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      window.removeEventListener('travel:admin-navigation', listener);
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, []);
  function go(href: string) {
    if (dirtyRef.current || busyRef.current || mediaBusyRef.current)
      setLeave({ commit: () => route(href) });
    else route(href);
  }
  function commitNavigation(commit: () => void) {
    navigationBypass.current = true;
    try {
      commit();
    } finally {
      navigationBypass.current = false;
    }
  }
  async function ensurePage() {
    if (detailRef.current) return detailRef.current;
    if (!creation.current)
      creation.current = props.api
        .send<AdLandingDetail>(basePath(props.appId), 'POST', { title: contentRef.current.title })
        .then((value) => {
          if (alive.current) {
            applyDetail(value, false);
            setSaved(JSON.stringify(value.page.draftContent));
          }
          return value;
        })
        .finally(() => {
          creation.current = undefined;
        });
    return creation.current;
  }
  async function saveDraft() {
    const original = JSON.stringify(contentRef.current);
    const parsed = parseAdLandingContent(contentRef.current);
    const current = await ensurePage();
    if (!alive.current) throw new Error('页面已切换，请重新打开草稿继续编辑');
    const value = await props.api.send<AdLandingDetail>(
      pagePath(props.appId, current.page.id),
      'PUT',
      {
        expectedDraftVersion: current.page.draftVersion,
        content: parsed,
      },
    );
    if (alive.current) {
      // Saving must never discard content typed while a request was in flight.
      if (value.page.appId !== props.appId || value.page.id !== current.page.id)
        throw new Error('保存结果归属不一致，当前输入已保留，请重新读取。');
      detailRef.current = value;
      setDetail(value);
      setSaved(JSON.stringify(value.page.draftContent));
      if (JSON.stringify(contentRef.current) === original) {
        contentRef.current = value.page.draftContent;
        setContent(value.page.draftContent);
        dirtyRef.current = false;
      }
    }
    return value;
  }
  async function perform(kind: 'save' | 'publish', targetAppId?: string) {
    const lockedTarget = detailRef.current?.page.currentPublishedVersionNumber != null;
    if (
      busyRef.current ||
      mediaBusyRef.current ||
      (kind === 'save'
        ? !props.canManage
        : lockedTarget
          ? !props.canPublish
          : !props.canManage && !props.canPublish)
    )
      return;
    if (kind === 'publish' && !targetAppId) {
      setError('请选择发布小程序。');
      publishTargetInput.current?.focus();
      return;
    }
    if (
      kind === 'publish' &&
      !lockedTarget &&
      !publishTargets.items.some((target) => target.id === targetAppId)
    ) {
      setError('所选小程序当前不可发布，请重新读取后再选择。');
      return;
    }
    if (validationError) {
      setError(`${validationError}；当前输入仍保留在编辑器内。`);
      return;
    }
    busyRef.current = true;
    setAction(kind);
    setError('');
    setFeedback('');
    try {
      if (kind === 'publish')
        validateAdLandingForPublish(parseAdLandingContent(contentRef.current));
      let current = detailRef.current;
      if (kind === 'save' || contentDirtyRef.current || !current) {
        if (!props.canManage)
          throw new Error('当前有未保存修改，请由有编辑权限的人员保存后再发布。');
        current = await saveDraft();
      }
      if (!current) throw new Error('页面保存未完成，请重试。');
      if (kind === 'publish') {
        const body = {
          expectedDraftVersion: current.page.draftVersion,
          expectedPublishedVersion: current.page.currentPublishedVersionNumber ?? 0,
          targetAppId,
        };
        const fingerprint = JSON.stringify({
          sourceAppId: props.appId,
          pageId: current.page.id,
          body,
        });
        if (publishAttempt.current?.fingerprint !== fingerprint)
          publishAttempt.current = { fingerprint, key: crypto.randomUUID() };
        const originalId = current.page.id;
        current = await props.api.request<AdLandingDetail>(
          `${pagePath(props.appId, originalId)}/publish`,
          {
            method: 'POST',
            headers: { 'Idempotency-Key': publishAttempt.current.key },
            body: JSON.stringify(body),
          },
        );
        const publishedVersion = current.page.currentPublishedVersionNumber;
        if (
          current.page.appId !== targetAppId ||
          !current.page.id ||
          typeof publishedVersion !== 'number' ||
          !Number.isInteger(publishedVersion) ||
          publishedVersion < 1 ||
          !['PUBLISHED', 'OFFLINE', 'ARCHIVED'].includes(current.page.status) ||
          current.page.relocatedTo
        )
          throw new Error('发布结果与所选小程序不一致，已停止跳转，请重新读取核对。');
        const samePage = current.page.appId === props.appId && current.page.id === originalId;
        if (alive.current && samePage) applyDetail(current, true);
        if (!alive.current) return;
        setSaved(JSON.stringify(contentRef.current));
        setTargetTouched(false);
        dirtyRef.current = false;
        busyRef.current = false;
        setAction('');
        setFeedback(
          current.page.status === 'PUBLISHED'
            ? '已保存并发布。'
            : current.page.status === 'OFFLINE'
              ? '发布已完成，页面目前已停用。'
              : '发布已完成，页面目前已归档。',
        );
        publishAttempt.current = undefined;
        commitNavigation(() =>
          route(`/admin/ad-landings/${encodeURIComponent(current!.page.id)}`, current!.page.appId),
        );
        return;
      }
      if (!alive.current) return;
      setTargetTouched(false);
      setFeedback('已保存，等待有发布权限的人员发布。');
      if (props.mode === 'new')
        commitNavigation(() => route(`/admin/ad-landings/${encodeURIComponent(current.page.id)}`));
    } catch (cause) {
      if (alive.current) setError(message(cause));
    } finally {
      busyRef.current = false;
      if (alive.current) setAction('');
    }
  }
  async function upload(file: File): Promise<AdLandingEditorAsset> {
    if (!props.canManage) throw new Error('当前账号没有上传权限');
    const image = ['image/jpeg', 'image/png'].includes(file.type),
      video = file.type === 'video/mp4';
    if (!image && !video) throw new Error('请选择 JPG、PNG 图片或 MP4 视频');
    if (
      !file.size ||
      file.size > (video ? AD_LANDING_LIMITS.videoBytes : AD_LANDING_LIMITS.imageBytes)
    )
      throw new Error(video ? '请选择 50MB 内的 MP4 视频' : '请选择 10MB 内的 JPG / PNG 图片');
    const current = await ensurePage();
    if (!alive.current) throw new Error('页面已切换，请重新打开页面继续上传');
    const form = new FormData();
    form.append('file', file);
    const asset = await props.api.upload<AdLandingAssetDto>(
      `${pagePath(props.appId, current.page.id)}/assets`,
      form,
      crypto.randomUUID(),
    );
    if (!alive.current) throw new Error('页面已切换，请重新打开页面查看已上传文件');
    if (
      asset.appId !== props.appId ||
      asset.pageId !== current.page.id ||
      asset.state !== 'READY' ||
      asset.kind !== (video ? 'VIDEO' : 'IMAGE')
    )
      throw new Error('素材归属校验失败，请重试');
    const url = URL.createObjectURL(file);
    urls.current.set(asset.id, url);
    setAssetUrls((value) => ({ ...value, [asset.id]: url }));
    const previous = detailRef.current ?? current;
    applyDetail(
      { ...previous, assets: [...previous.assets.filter((value) => value.id !== asset.id), asset] },
      false,
    );
    return { id: asset.id, kind: asset.kind, name: asset.originalName, url };
  }
  const closePreview = () => setPreview(false),
    closeLeave = () => {
      setLeave(undefined);
    };
  if (loading)
    return (
      <div className="ad-empty" role="status">
        正在读取页面内容…
      </div>
    );
  if (loadError)
    return (
      <div className="ad-alert ad-alert--error" role="alert">
        {loadError}{' '}
        <button type="button" onClick={() => setReload((value) => value + 1)}>
          重新读取
        </button>
      </div>
    );
  const readOnly = !props.canManage || detail?.page.status === 'ARCHIVED';
  const archived = detail?.page.status === 'ARCHIVED';
  const canSaveOnly =
    props.canManage &&
    !archived &&
    (targetLocked
      ? !props.canPublish
      : !publishTargets.loading && !publishTargets.error && publishTargets.items.length === 0);
  const showPublish =
    !archived &&
    !canSaveOnly &&
    (targetLocked ? props.canPublish : props.canManage || props.canPublish);
  const firstImage = content.blocks.find((block) => block.type === 'image');
  const coverId =
    content.coverAssetId || (firstImage?.type === 'image' ? firstImage.assetId : null);
  const cover = coverId ? assetUrls[coverId] : undefined;
  return (
    <>
      <header className="ad-heading">
        <div>
          <button type="button" className="ad-back" onClick={() => go('/admin/ad-landings')}>
            ← 返回落地页管理
          </button>
          <h1>{props.mode === 'new' ? '新建落地页' : '编辑落地页'}</h1>
        </div>
      </header>
      {error && (
        <p className="ad-alert ad-alert--error" role="alert">
          {error}
        </p>
      )}
      <section className="ad-card" aria-label="页面信息">
        <div className="ad-card-heading">
          <h2>页面信息</h2>
        </div>
        <label className="ad-field">
          <span>
            发布小程序{!targetLocked && showPublish && <i className="ad-required">必填</i>}
          </span>
          {targetLocked ? (
            <input aria-label="发布小程序" readOnly value={props.appName || '当前小程序'} />
          ) : (
            <select
              ref={publishTargetInput}
              aria-label="发布小程序"
              value={publishTargetId}
              disabled={
                blocked ||
                publishTargets.loading ||
                !publishTargets.items.length ||
                archived ||
                (!props.canManage && !props.canPublish)
              }
              onChange={(event) => {
                setPublishTargetId(event.target.value);
                setTargetTouched(true);
                setError('');
              }}
            >
              <option value="">
                {publishTargets.loading
                  ? '正在读取小程序…'
                  : !publishTargets.error && !publishTargets.items.length
                    ? '暂无可发布小程序'
                    : '请选择小程序'}
              </option>
              {publishTargets.items.map((target) => (
                <option key={target.id} value={target.id}>
                  {target.displayName}
                </option>
              ))}
            </select>
          )}
        </label>
        {!targetLocked && publishTargets.error && (
          <p className="ad-target-error" role="alert">
            {publishTargets.error}{' '}
            <button type="button" onClick={() => setTargetRevision((value) => value + 1)}>
              重试
            </button>
          </p>
        )}
        <label className="ad-field">
          <span>
            页面标题<i className="ad-required">必填</i>
          </span>
          <input
            aria-label="页面标题"
            value={content.title}
            maxLength={100}
            placeholder="例如：潮汕包车游，轻松玩遍好风景"
            disabled={readOnly || blocked}
            onChange={(event) => setContent((value) => ({ ...value, title: event.target.value }))}
          />
        </label>
        <label className="ad-field">
          <span>
            简介<span className="ad-muted">（选填）</span>
          </span>
          <textarea
            aria-label="页面简介"
            value={content.summary}
            maxLength={500}
            rows={2}
            placeholder="例如：专车接送，自由安排行程，随时咨询客服"
            disabled={readOnly || blocked}
            onChange={(event) => setContent((value) => ({ ...value, summary: event.target.value }))}
          />
        </label>
      </section>
      <section className="ad-card" aria-label="页面内容">
        <div className="ad-card-heading">
          <h2>页面内容</h2>
        </div>
        <Suspense fallback={<p role="status">正在加载正文编辑器…</p>}>
          <AdLandingEditor
            blocks={content.blocks}
            assets={assets}
            disabled={readOnly || Boolean(action)}
            onChange={(blocks) => setContent((value) => ({ ...value, blocks }))}
            onUpload={upload}
            onPreviewAsset={previewAsset}
            onBusyChange={(value) => {
              mediaBusyRef.current = value;
              setMediaBusy(value);
            }}
            onValidationChange={setValidationError}
          />
        </Suspense>
        <p className="ad-muted" style={{ marginTop: 10 }}>
          图片 JPG/PNG，10MB 内；视频 MP4，50MB、5 分钟内。
        </p>
        {mediaError && (
          <p className="ad-alert ad-alert--error" role="alert">
            {mediaError}{' '}
            <button
              type="button"
              onClick={() => {
                setMediaError('');
                setDetail((value) => (value ? { ...value, assets: [...value.assets] } : value));
              }}
            >
              重新读取素材
            </button>
          </p>
        )}
      </section>
      <section className="ad-card" aria-label="分享封面">
        <div className="ad-card-heading">
          <h2>
            分享封面<span className="ad-muted">（选填）</span>
          </h2>
        </div>
        <div className="ad-cover">
          {cover && <img src={cover} alt="当前分享封面" />}
          <div>
            <label className="ad-field">
              <span>选择已上传图片</span>
              <select
                aria-label="分享封面"
                value={content.coverAssetId ?? ''}
                disabled={readOnly || blocked}
                onChange={(event) =>
                  setContent((value) => ({ ...value, coverAssetId: event.target.value || null }))
                }
              >
                <option value="">自动使用第一张图片</option>
                {assets
                  .filter((asset) => asset.kind === 'IMAGE')
                  .map((asset) => (
                    <option key={asset.id} value={asset.id}>
                      {asset.name}
                    </option>
                  ))}
              </select>
            </label>
          </div>
        </div>
      </section>
      {detail?.page.status === 'PUBLISHED' && (
        <AdLandingPromotion api={props.api} appId={props.appId} page={detail.page} />
      )}
      <footer className="ad-savebar">
        <p role="status">
          {mediaBusy
            ? '正在上传素材…'
            : action
              ? '正在保存…'
              : dirty
                ? '有修改未保存'
                : feedback || (detail ? '已保存' : '')}
        </p>
        <div className="ad-inline-actions">
          <button
            type="button"
            onClick={() => setPreview(true)}
            disabled={blocked || Boolean(validationError)}
          >
            预览
          </button>
          {canSaveOnly && (
            <button
              type="button"
              className="ad-primary"
              disabled={
                blocked ||
                Boolean(validationError) ||
                (!contentDirty && Boolean(detail)) ||
                readOnly
              }
              onClick={() => void perform('save')}
            >
              {action === 'save' ? '保存中…' : '保存'}
            </button>
          )}
          {showPublish && (
            <button
              type="button"
              className="ad-primary"
              disabled={
                blocked ||
                Boolean(validationError) ||
                (!targetLocked && (publishTargets.loading || Boolean(publishTargets.error)))
              }
              onClick={() =>
                void perform('publish', targetLocked ? detail!.page.appId : publishTargetId)
              }
            >
              {action === 'publish' ? '保存发布中…' : '保存并发布'}
            </button>
          )}
        </div>
      </footer>
      {preview && (
        <Preview
          content={content}
          assets={assets}
          onClose={closePreview}
          onPreviewAsset={previewAsset}
        />
      )}
      {leave && (
        <Dialog title={blocked ? '操作正在进行' : '修改还没有保存'} onClose={closeLeave}>
          <p>
            {blocked
              ? '请等待保存或上传完成后再离开，避免遗漏刚才的操作。'
              : '离开会放弃尚未保存的修改。'}
          </p>
          <div className="ad-inline-actions">
            <button type="button" onClick={closeLeave}>
              继续编辑
            </button>
            {!blocked && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    const commit = leave.commit;
                    dirtyRef.current = false;
                    setLeave(undefined);
                    commitNavigation(commit);
                  }}
                >
                  放弃修改
                </button>
              </>
            )}
          </div>
        </Dialog>
      )}
    </>
  );
}

export function AdLandingPages(props: AdLandingPagesProps) {
  if (!props.appId)
    return (
      <section className="ad-landing-workspace">
        <div className="ad-empty">
          <h2>先选择当前小程序</h2>
          <p>请选择小程序查看落地页。</p>
        </div>
      </section>
    );
  return (
    <section className="ad-landing-workspace" aria-label="广告落地页工作区">
      {props.mode === 'list' ? (
        <AdLandingListPage key={props.appId} {...props} />
      ) : (
        <AdLandingEditorPage key={`${props.appId}:${props.landingId ?? 'new'}`} {...props} />
      )}
    </section>
  );
}
